import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setPushSupported(true);
      navigator.serviceWorker.ready.then((reg) => {
        reg.pushManager.getSubscription().then((sub) => {
          setPushEnabled(!!sub);
        });
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const subscribePush = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      await axios.post(`${BACKEND_URL}/api/push/subscribe`, {
        subscription: subscription.toJSON()
      }, { withCredentials: true });

      setPushEnabled(true);
    } catch (e) {
      console.error('Push subscribe failed:', e);
    }
  };

  const unsubscribePush = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) await subscription.unsubscribe();

      await axios.post(`${BACKEND_URL}/api/push/unsubscribe`, {}, { withCredentials: true });
      setPushEnabled(false);
    } catch (e) {
      console.error('Push unsubscribe failed:', e);
    }
  };

  const markRead = async (id) => {
    await axios.put(`${BACKEND_URL}/api/notifications/${id}/read`, {}, { withCredentials: true });
    fetchNotifications();
  };

  const markAllRead = async () => {
    await axios.put(`${BACKEND_URL}/api/notifications/read-all`, {}, { withCredentials: true });
    fetchNotifications();
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString();
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'round_started': return 'bg-green-500';
      case 'results_published': return 'bg-orange-500';
      case 'selection_window': return 'bg-blue-500';
      case 'admin': return 'bg-purple-500';
      default: return 'bg-zinc-500';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        data-testid="notification-bell"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-zinc-400 hover:text-white transition-colors"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 bg-primary text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-full mt-2 w-80 bg-zinc-900 border border-zinc-700 shadow-xl z-50 max-h-96 overflow-hidden flex flex-col"
        >
          <div className="flex items-center justify-between p-3 border-b border-zinc-700">
            <span className="font-heading font-bold text-sm">Notifications</span>
            <div className="flex items-center gap-2">
              {pushSupported && (
                <button
                  data-testid="push-toggle"
                  onClick={pushEnabled ? unsubscribePush : subscribePush}
                  className={`text-xs px-2 py-1 rounded ${pushEnabled ? 'bg-green-600/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}
                >
                  {pushEnabled ? 'Push ON' : 'Push OFF'}
                </button>
              )}
              {unreadCount > 0 && (
                <button
                  data-testid="mark-all-read"
                  onClick={markAllRead}
                  className="text-xs text-primary hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.notification_id}
                  data-testid={`notification-item-${n.notification_id}`}
                  onClick={() => !n.read && markRead(n.notification_id)}
                  className={`w-full text-left p-3 border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors ${!n.read ? 'bg-zinc-800/30' : ''}`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.read ? getTypeColor(n.type) : 'bg-zinc-600'}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${!n.read ? 'text-white' : 'text-zinc-400'}`}>
                        {n.title}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{n.body}</p>
                      <p className="text-xs text-zinc-600 mt-1">{formatTime(n.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
